import React, { useMemo } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import type { HTMLAttributes } from 'react';
import type { TSchema } from 'types/items';
import type { Configuration } from '../../Schema/schema.types';
import { getPluralLabel } from '../utils/pluralize';

interface Props extends HTMLAttributes<HTMLDivElement> {
  configuration: Configuration;
  handleExport: () => void;
  handleRefresh: () => void;
  mobileMenuId: string;
  mobileMoreAnchorEl: null | HTMLElement;
  onMobileMenuClose: () => void;
  rowsSelected: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: TSchema<any>;
  setShowConfirmDelete: (show: boolean) => void;
  setShowImportDialog: (show: boolean) => void;
  setShowPayrollExportDialog: (show: boolean) => void;
  setShowNewDialog: (show: boolean) => void;
  setShowUpdateDialog: (show: boolean) => void;
}

export default function MobileMenu({
  configuration,
  handleExport,
  handleRefresh,
  mobileMenuId,
  mobileMoreAnchorEl,
  onMobileMenuClose,
  rowsSelected,
  schema,
  setShowConfirmDelete,
  setShowImportDialog,
  setShowPayrollExportDialog,
  setShowNewDialog,
  setShowUpdateDialog,
}: Props) {
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const pluralItemLabel = getPluralLabel(configuration.itemLabel);

  const showAddMenuItem = useMemo(
    () => schema?.permissions?.create && !configuration.addDisabled,
    [schema, configuration]
  );
  const showDeleteMenuItem = useMemo(
    () =>
      schema?.permissions?.delete &&
      !configuration.deleteDisabled &&
      !configuration.checkboxSelectionDisabled,
    [schema, configuration]
  );
  const showBulkUpdateMenuItem = useMemo(
    () =>
      schema?.permissions?.update &&
      !configuration.bulkUpdateDisabled &&
      !configuration.checkboxSelectionDisabled,
    [schema, configuration]
  );

  const showExportMenuItem = useMemo(
    () => !configuration.exportDisabled && !configuration.checkboxSelectionDisabled,
    [configuration]
  );

  const showImportMenuItem = useMemo(
    () =>
      schema?.permissions?.create &&
      !configuration.addDisabled &&
      !configuration.importDisabled,
    [schema, configuration]
  );

  const showPayrollExportMenuItem = useMemo(
    () => Boolean(configuration.customDialogs?.payrollExport),
    [configuration]
  );

  return (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={onMobileMenuClose}
    >
      <MenuItem onClick={handleRefresh}>
        <IconButton
          size="large"
          edge="start"
          color="inherit"
          aria-label="refresh-button"
          sx={{ mr: 2 }}
        >
          <Icon>refresh</Icon>
        </IconButton>

        <p>Refresh</p>
      </MenuItem>

      {showAddMenuItem && (
        <MenuItem onClick={() => setShowNewDialog(true)}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="add-button"
            sx={{ mr: 2 }}
          >
            <Icon>add</Icon>
          </IconButton>

          <p>{`New ${configuration.itemLabel}`}</p>
        </MenuItem>
      )}

      {showDeleteMenuItem && (
        <MenuItem
          disabled={!rowsSelected}
          onClick={() => setShowConfirmDelete(true)}
        >
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="delete-button"
            sx={{ mr: 2 }}
          >
            <Icon>delete</Icon>
          </IconButton>

          <p>{`Delete ${pluralItemLabel}`}</p>
        </MenuItem>
      )}

      {showBulkUpdateMenuItem && (
        <MenuItem
          disabled={!rowsSelected}
          onClick={() => setShowUpdateDialog(true)}
        >
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="update-button"
            sx={{ mr: 2 }}
          >
            <Icon>edit_note</Icon>
          </IconButton>

          <p>{`Update ${pluralItemLabel}`}</p>
        </MenuItem>
      )}

      {showExportMenuItem && (
        <MenuItem disabled={!rowsSelected} onClick={handleExport}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="export-button"
            sx={{ mr: 2 }}
          >
            <Icon>download_file</Icon>
          </IconButton>

          <p>{`Export ${pluralItemLabel}`}</p>
        </MenuItem>
      )}

      {showImportMenuItem && (
        <MenuItem onClick={() => setShowImportDialog(true)}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="import-button"
            sx={{ mr: 2 }}
          >
            <Icon>upload_file</Icon>
          </IconButton>

          <p>{`Import ${pluralItemLabel}`}</p>
        </MenuItem>
      )}

      {showPayrollExportMenuItem && (
        <MenuItem onClick={() => setShowPayrollExportDialog(true)}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="payroll-export-button"
            sx={{ mr: 2 }}
          >
            <Icon>payments</Icon>
          </IconButton>

          <p>Payroll Export</p>
        </MenuItem>
      )}
    </Menu>
  );
}
